"use client";

import * as React from "react";
import Image from "next/image";
import type { Review } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useReviewFilter } from "@/components/reviews";
import { ReviewList } from "./ReviewList";
import { ReviewEditor } from "./ReviewEditor";
import { ReviewSummary } from "./ReviewSummary";
import { ReviewPanel } from "./ReviewPanel";
import { BookOpen, Ghost, Plus } from "lucide-react";

import {
  AIErrorCard,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Header,
  Modal,
  PageShell,
  SearchBar,
  Select,
  Skeleton,
  TabBar,
} from "@/components/ui";
import { useQueryParam } from "@/lib/useQueryParam";

type SortKey = "newest" | "oldest" | "title";
type DetailMode = "summary" | "edit";

export type ReviewsPageProps = {
  reviews: Review[] | null | undefined;
  loading?: boolean;
  error?: Error | string | null;
  onRetry?: (() => void) | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onCreate: () => void;
  onRename: (id: string, nextTitle: string) => void;
  onDelete?: (id: string) => void;
  onChangeNotes?: (id: string, nextNotes: string) => void;
  onChangeTags?: (id: string, nextTags: string[]) => void;
  onChangeMeta?: (id: string, partial: Partial<Review>) => void;
};

export function ReviewsPage({
  reviews,
  loading,
  error = null,
  onRetry = null,
  selectedId,
  onSelect,
  onCreate,
  onRename,
  onDelete,
  onChangeNotes,
  onChangeTags,
  onChangeMeta,
}: ReviewsPageProps) {
  const intentParam = useQueryParam("intent", "");
  const [q, setQ] = React.useState("");
  const [sort, setSort] = React.useState<SortKey>("newest");
  const [detailMode, setDetailMode] = React.useState<DetailMode>("summary");
  const [intentApplied, setIntentApplied] = React.useState(false);
  const [deleteTargetId, setDeleteTargetId] = React.useState<string | null>(null);

  const isLoading = (loading ?? (typeof reviews === "undefined")) === true;
  const errorMessage = React.useMemo(() => {
    if (error instanceof Error) {
      const message = error.message?.trim();
      if (message?.length) return message;
    }
    if (typeof error === "string") {
      const trimmed = error.trim();
      if (trimmed.length > 0) return trimmed;
    }
    if (error && typeof error !== "object") {
      return String(error);
    }
    if (reviews === null) {
      return "We couldn’t load your reviews.";
    }
    return null;
  }, [error, reviews]);
  const isErrored = Boolean(errorMessage);
  const allowInteractions = !isLoading && !isErrored;

  const commitCreateReview = React.useCallback(() => {
    setQ("");
    setSort("newest");
    setDetailMode("edit");
    onCreate();
  }, [onCreate]);

  const tryCreateReview = React.useCallback(() => {
    if (!allowInteractions) return false;
    commitCreateReview();
    return true;
  }, [allowInteractions, commitCreateReview]);

  React.useEffect(() => {
    if (intentParam === "create-review") {
      if (!intentApplied) {
        const created = tryCreateReview();
        if (created) {
          setIntentApplied(true);
        }
      }
      return;
    }
    if (intentApplied) {
      setIntentApplied(false);
    }
  }, [intentParam, intentApplied, tryCreateReview]);

  const base = React.useMemo<Review[]>(
    () => (Array.isArray(reviews) ? reviews : []),
    [reviews],
  );

  const filtered = useReviewFilter(base, q, sort);
  const totalCount = base.length;
  const listReviews = allowInteractions ? filtered : [];
  const filteredCount = allowInteractions ? filtered.length : 0;
  const hasReviews = allowInteractions && totalCount > 0;
  const deleteTarget = React.useMemo(
    () => base.find((review) => review.id === deleteTargetId) ?? null,
    [base, deleteTargetId],
  );

  const handleRequestDelete = React.useCallback(
    (id: string) => {
      if (!onDelete) return;
      setDeleteTargetId(id);
    },
    [onDelete],
  );

  const handleConfirmDelete = React.useCallback(() => {
    if (deleteTargetId && onDelete) {
      onDelete(deleteTargetId);
    }
    setDeleteTargetId(null);
  }, [deleteTargetId, onDelete]);

  const handleDismissDelete = React.useCallback(() => {
    setDeleteTargetId(null);
  }, []);

  const active = React.useMemo(
    () => {
      if (!allowInteractions) return null;
      return base.find((r) => r.id === selectedId) ?? null;
    },
    [allowInteractions, base, selectedId],
  );
  const panelClass = "mx-auto";
  const detailBaseId = active ? `review-${active.id}` : "review-detail";
  const headerHeadingId = React.useId();
  const sortLabelId = React.useId();
  const emptySearchDescriptionId = React.useId();
  const emptySearchTooltipId = React.useId();
  const sortItems = React.useMemo(
    () => [
      { value: "newest", label: "Newest" },
      { value: "oldest", label: "Oldest" },
      { value: "title", label: "Title" },
    ],
    [],
  );
  const deleteTitle = deleteTarget?.title?.trim() || "this review";

  const shouldShowEmptySearchHelper = allowInteractions && totalCount === 0;
  const [isSearchTooltipOpen, setIsSearchTooltipOpen] = React.useState(false);

  const closeSearchTooltip = React.useCallback(() => {
    setIsSearchTooltipOpen(false);
  }, []);

  const openSearchTooltip = React.useCallback(() => {
    if (!shouldShowEmptySearchHelper) return;
    setIsSearchTooltipOpen(true);
  }, [shouldShowEmptySearchHelper]);

  const handleSearchFocus = React.useCallback<
    React.FocusEventHandler<HTMLInputElement>
  >(() => {
    openSearchTooltip();
  }, [openSearchTooltip]);

  const handleSearchBlur = React.useCallback<
    React.FocusEventHandler<HTMLInputElement>
  >(() => {
    closeSearchTooltip();
  }, [closeSearchTooltip]);

  const handleSearchMouseEnter = React.useCallback<
    React.MouseEventHandler<HTMLInputElement>
  >(() => {
    openSearchTooltip();
  }, [openSearchTooltip]);

  const handleSearchMouseLeave = React.useCallback<
    React.MouseEventHandler<HTMLInputElement>
  >((event) => {
      const target = event.currentTarget;
      if (target instanceof HTMLElement && target === document.activeElement) {
        return;
      }
      closeSearchTooltip();
    },
    [closeSearchTooltip],
  );

  const handleSearchKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape" || event.key === "Esc") {
        closeSearchTooltip();
      }
    },
    [closeSearchTooltip],
  );

  React.useEffect(() => {
    if (!shouldShowEmptySearchHelper) {
      setIsSearchTooltipOpen(false);
      return undefined;
    }

    if (!isSearchTooltipOpen) {
      return undefined;
    }

    const handleWindowKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Esc") {
        closeSearchTooltip();
      }
    };

    window.addEventListener("keydown", handleWindowKeyDown);
    return () => window.removeEventListener("keydown", handleWindowKeyDown);
  }, [shouldShowEmptySearchHelper, isSearchTooltipOpen, closeSearchTooltip]);

  const searchLabel = isLoading
    ? "Search reviews (temporarily unavailable)"
    : isErrored
      ? "Search reviews (temporarily unavailable)"
      : !hasReviews
        ? "Search reviews (add a review to start searching)"
        : "Search reviews";
  const searchDescription = React.useMemo(() => {
    const ids: string[] = [];
    if (shouldShowEmptySearchHelper) {
      ids.push(emptySearchTooltipId);
    }
    if (!hasReviews) {
      ids.push(emptySearchDescriptionId);
    }
    return ids.length > 0 ? ids.join(" ") : undefined;
  }, [shouldShowEmptySearchHelper, emptySearchTooltipId, hasReviews, emptySearchDescriptionId]);
  const searchDisabled = !allowInteractions;
  const searchTooltip = shouldShowEmptySearchHelper ? (
    <div
      id={emptySearchTooltipId}
      role="tooltip"
      className={cn(
        "pointer-events-none absolute left-1/2 top-full z-20 w-max max-w-[min(22rem,calc(100vw-var(--space-6)))] -translate-x-1/2",
        "rounded-[var(--radius-lg)] border border-card-hairline bg-[hsl(var(--surface-2))] px-[var(--space-3)] py-[var(--space-2)]",
        "text-label font-medium text-foreground shadow-depth-soft",
        "transition-[opacity,transform] duration-motion-sm ease-out motion-reduce:transition-none motion-reduce:transform-none",
        isSearchTooltipOpen
          ? "opacity-100 translate-y-[var(--space-2)]"
          : "pointer-events-none opacity-0 -translate-y-[var(--space-1)]",
      )}
      aria-hidden={isSearchTooltipOpen ? undefined : "true"}
    >
      <span className="block text-balance text-center leading-tight">
        Add a review to search.
      </span>
    </div>
  ) : null;

  const headerSearch = (
    <div className="relative w-full max-w-[28rem]">
      <SearchBar
        value={q}
        onValueChange={setQ}
        placeholder="Search reviews..."
        aria-label={searchLabel}
        aria-describedby={searchDescription}
        height="lg"
        debounceMs={300}
        variant="sunken"
        loading={isLoading}
        disabled={searchDisabled}
        className={shouldShowEmptySearchHelper ? "relative" : undefined}
        onFocus={shouldShowEmptySearchHelper ? handleSearchFocus : undefined}
        onBlur={shouldShowEmptySearchHelper ? handleSearchBlur : undefined}
        onMouseEnter={shouldShowEmptySearchHelper ? handleSearchMouseEnter : undefined}
        onMouseLeave={shouldShowEmptySearchHelper ? handleSearchMouseLeave : undefined}
        onKeyDown={shouldShowEmptySearchHelper ? handleSearchKeyDown : undefined}
      />
      {searchTooltip}
    </div>
  );

  return (
    <>
      <Header
        heading={<span id={headerHeadingId}>Reviews</span>}
        subtitle="Capture and learn from your past sprints."
        icon={<BookOpen className="opacity-80" />}
        variant="neo"
        underlineTone="brand"
        search={headerSearch}
        actions={
          <Button
            type="button"
            variant="default"
            size="md"
            className="whitespace-nowrap"
            onClick={commitCreateReview}
            disabled={!allowInteractions}
          >
            <Plus />
            <span>New review</span>
          </Button>
        }
      />

      <PageShell
        as="main"
        id="page-main"
        tabIndex={-1}
        grid
        className="py-[var(--space-6)]"
        contentClassName="gap-y-[var(--space-6)]"
        aria-labelledby={headerHeadingId}
      >
        <div className="col-span-full">
          {isLoading ? (
            <div className="flex flex-col gap-[var(--space-3)] sm:gap-[var(--space-4)] md:flex-row md:items-end md:justify-end">
              <Skeleton
                ariaHidden={false}
                role="status"
                aria-label="Loading sort control"
                className="h-[var(--control-h-lg)] w-full md:w-[16rem]"
                radius="md"
              />
            </div>
          ) : isErrored ? (
            <div className="space-y-[var(--space-3)]">
              <div className="flex flex-col gap-[var(--space-3)] sm:gap-[var(--space-4)] md:flex-row md:items-end md:justify-end">
                <div
                  className="flex w-full flex-col gap-[var(--space-1)] text-left md:w-[16rem]"
                  aria-labelledby={sortLabelId}
                >
                  <span
                    id={sortLabelId}
                    className="text-ui font-medium text-muted-foreground"
                  >
                    Sort
                  </span>
                  <Select
                    variant="animated"
                    label="Sort reviews"
                    hideLabel
                    value={sort}
                    onChange={(v) => setSort(v as SortKey)}
                    items={sortItems}
                    className="w-full"
                    size="lg"
                    disabled
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-[var(--space-2)]">
                <Button
                  type="button"
                  variant="default"
                  size="md"
                  className={cn("btn-glitch", "whitespace-nowrap")}
                  onClick={onRetry ?? undefined}
                  disabled={!onRetry}
                >
                  Retry sync
                </Button>
                <p className="text-ui text-danger" role="alert">
                  {errorMessage ?? "We couldn’t load your reviews. Retry to continue."}
                </p>
              </div>
            </div>
          ) : hasReviews ? (
            <div className="flex flex-col gap-[var(--space-3)] sm:gap-[var(--space-4)] md:flex-row md:items-end md:justify-end">
              <div
                className="flex w-full flex-col gap-[var(--space-1)] text-left md:w-[16rem]"
                aria-labelledby={sortLabelId}
              >
                <span
                  id={sortLabelId}
                  className="text-ui font-medium text-muted-foreground"
                >
                  Sort
                </span>
                <Select
                  variant="animated"
                  label="Sort reviews"
                  hideLabel
                  value={sort}
                  onChange={(v) => setSort(v as SortKey)}
                  items={sortItems}
                  className="w-full"
                  size="lg"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-[var(--space-2)]">
              <p
                id={emptySearchDescriptionId}
                className="text-ui text-muted-foreground"
              >
                Once you publish your first review, smart filters, tagging, and matchup search become available.
              </p>
            </div>
          )}
        </div>
        <div
          className={cn(
            "col-span-full",
            "grid grid-cols-1 items-start gap-[var(--space-4)] sm:gap-[var(--space-6)] lg:gap-[var(--space-8)] md:grid-cols-6 lg:grid-cols-12",
          )}
        >
          <nav
            aria-label="Review list"
            className="md:col-span-2 lg:col-span-4"
          >
              <ReviewList
                reviews={listReviews}
                selectedId={selectedId}
                loading={isLoading}
                error={errorMessage}
                onRetry={isErrored ? onRetry : null}
                onSelect={
                  allowInteractions
                    ? (id) => {
                        setDetailMode("summary");
                        onSelect(id);
                      }
                    : undefined
                }
                onRename={allowInteractions ? onRename : undefined}
                onDelete={
                  allowInteractions && onDelete ? handleRequestDelete : undefined
                }
                className="h-auto overflow-auto p-[var(--space-2)] md:h-[var(--content-viewport-height)]"
                header={
                  allowInteractions && filteredCount > 0
                    ? `${filteredCount} shown`
                    : undefined
                }
                hasAnyReviews={allowInteractions && totalCount > 0}
                onCreate={allowInteractions ? commitCreateReview : undefined}
                hoverRing
              />
          </nav>
          <div className="md:col-span-4 lg:col-span-8">
            {isLoading ? (
              <ReviewPanel
                aria-busy="true"
                className={cn(panelClass, "space-y-[var(--space-4)]")}
              >
                <div className="space-y-[var(--space-2)]">
                  <Skeleton
                    ariaHidden={false}
                    role="status"
                    aria-label="Loading review summary"
                    className="h-[var(--space-6)] w-2/3"
                    radius="sm"
                  />
                  <Skeleton className="w-3/4" />
                </div>
                <div className="grid gap-[var(--space-3)] sm:grid-cols-[var(--space-12)_1fr]">
                  <Skeleton
                    ariaHidden={false}
                    role="status"
                    aria-label="Loading review avatar"
                    className="h-[var(--space-12)] w-[var(--space-12)]"
                    radius="full"
                  />
                  <div className="space-y-[var(--space-2)]">
                    <Skeleton className="w-2/3" />
                    <Skeleton className="w-3/4" />
                    <Skeleton className="w-full" />
                  </div>
                </div>
                <Skeleton
                  ariaHidden={false}
                  role="status"
                  aria-label="Loading review actions"
                  className="h-[var(--space-10)] w-full"
                  radius="sm"
                />
              </ReviewPanel>
            ) : isErrored ? (
              <ReviewPanel aria-live="polite" className={panelClass}>
                <AIErrorCard
                  title="Review detail unavailable"
                  description={errorMessage ?? "We couldn’t load your review details."}
                  hint="Retry the sync to continue editing or summarizing your matches."
                  retryLabel="Retry sync"
                  onRetry={onRetry ?? undefined}
                  className="w-full border-none bg-transparent p-0 shadow-none"
                />
              </ReviewPanel>
            ) : !hasReviews ? (
              <Card
                role="status"
                aria-live="polite"
                className={cn(
                  panelClass,
                  "relative isolate overflow-hidden border-none bg-[hsl(var(--surface-2))] text-left shadow-neo",
                )}
                depth="raised"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-[color-mix(in_srgb,hsl(var(--surface-2))_70%,hsl(var(--primary-2))_15%)] via-[hsl(var(--surface-2))] to-[color-mix(in_srgb,hsl(var(--surface-2))_70%,hsl(var(--primary-3))_20%)]"
                />
                <div className="grid items-center gap-[var(--space-4)] md:grid-cols-[1.2fr_auto]">
                  <div className="space-y-[var(--space-3)]">
                    <CardHeader className="space-y-[var(--space-2)] p-0">
                      <CardTitle className="text-balance">Capture your first review</CardTitle>
                      <CardDescription className="text-pretty">
                        Agnes has a fresh page ready. Log your latest match to unlock summaries, tagging, and focus tracking.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <div className="flex flex-wrap items-center gap-[var(--space-2)] text-muted-foreground">
                        <span className="rounded-full bg-[hsl(var(--surface-3))] px-[var(--space-2)] py-[var(--space-1)] text-label font-medium text-foreground">
                          Guidance
                        </span>
                        <span className="text-ui">
                          Capture notes to see AI summaries and track what to improve next time.
                        </span>
                      </div>
                    </CardContent>
                    <CardFooter className="flex flex-wrap gap-[var(--space-2)] p-0">
                      <Button
                        type="button"
                        variant="default"
                        size="md"
                        className="btn-glitch"
                        onClick={commitCreateReview}
                        disabled={!allowInteractions}
                      >
                        Capture first review
                      </Button>
                    </CardFooter>
                  </div>
                  <div className="relative mx-auto h-[min(16rem,40vw)] w-full max-w-[18rem]">
                    <Image
                      src="/images/agnes-writing-notes.svg"
                      alt="Agnes writing review notes"
                      fill
                      sizes="(min-width: 1024px) 18rem, 60vw"
                      className="object-contain"
                      priority={false}
                    />
                  </div>
                </div>
              </Card>
            ) : !active ? (
              <ReviewPanel
                aria-live="polite"
                className={cn(
                  panelClass,
                  "relative flex flex-col items-center justify-center gap-[var(--space-3)] overflow-hidden",
                  "text-center text-ui text-muted-foreground",
                )}
              >
                <span
                  aria-hidden
                  className="glitch-rail pointer-events-none absolute inset-y-[var(--space-2)] left-1/2 hidden w-[var(--spacing-1)] -translate-x-1/2 rounded-full opacity-80 mix-blend-screen sm:block"
                />
                <span
                  aria-hidden
                  data-text=""
                  className="glitch-anim inline-flex items-center justify-center rounded-full border border-border/40 bg-card/70 p-[var(--space-3)] text-muted-foreground motion-reduce:animate-none"
                >
                  <Ghost
                    aria-hidden
                    focusable="false"
                    className="size-[var(--space-6)]"
                  />
                </span>
                <div className="space-y-[var(--space-1)]">
                  <p className="text-card-foreground">Select a review from the list to see its summary.</p>
                  <p>Use New review to document another match when you&rsquo;re ready.</p>
                </div>
              </ReviewPanel>
            ) : (
              <div className="space-y-[var(--space-4)]">
                <TabBar<DetailMode>
                  items={[
                    { key: "summary", label: "Summary" },
                    { key: "edit", label: "Edit" },
                  ]}
                  value={detailMode}
                  onValueChange={setDetailMode}
                  ariaLabel="Review detail mode"
                  idBase={detailBaseId}
                />
                <div
                  id={`${detailBaseId}-summary-panel`}
                  role="tabpanel"
                  aria-labelledby={`${detailBaseId}-summary-tab`}
                  hidden={detailMode !== "summary"}
                  tabIndex={detailMode === "summary" ? 0 : -1}
                >
                  {detailMode === "summary" ? (
                    <ReviewPanel className={panelClass}>
                      <ReviewSummary
                        key={`summary-${active.id}`}
                        review={active}
                        onEdit={() => setDetailMode("edit")}
                      />
                    </ReviewPanel>
                  ) : null}
                </div>
                <div
                  id={`${detailBaseId}-edit-panel`}
                  role="tabpanel"
                  aria-labelledby={`${detailBaseId}-edit-tab`}
                  hidden={detailMode !== "edit"}
                  tabIndex={detailMode === "edit" ? 0 : -1}
                >
                  {detailMode === "edit" ? (
                    <ReviewPanel className={panelClass}>
                      <ReviewEditor
                        key={`editor-${active.id}`}
                        review={active}
                        onChangeNotes={(value: string) =>
                          onChangeNotes?.(active.id, value)
                        }
                        onChangeTags={(values: string[]) =>
                          onChangeTags?.(active.id, values)
                        }
                        onRename={(title: string) =>
                          onRename(active.id, title)
                        }
                        onChangeMeta={(partial: Partial<Review>) =>
                          onChangeMeta?.(active.id, partial)
                        }
                        onDone={() => setDetailMode("summary")}
                        onDelete={
                          onDelete
                            ? () => handleRequestDelete(active.id)
                            : undefined
                        }
                      />
                    </ReviewPanel>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      </PageShell>
      <Modal
        open={Boolean(deleteTargetId)}
        onOpenChange={(open) => {
          if (!open) {
            handleDismissDelete();
          }
        }}
        title="Delete review?"
        description={`This permanently removes “${deleteTitle}” and its notes.`}
        actions={(
          <>
            <Button
              type="button"
              size="sm"
              variant="default"
              tone="danger"
              onClick={handleConfirmDelete}
              disabled={!deleteTarget}
            >
              Delete review
            </Button>
            <Button type="button" variant="quiet" size="sm" onClick={handleDismissDelete}>
              Cancel
            </Button>
          </>
        )}
      >
        <p className="text-ui text-muted-foreground">
          This action can’t be undone. Export or duplicate any notes you need before deleting.
        </p>
      </Modal>
    </>
  );
}
